import useFeatureToggle from '@/hooks/useFeatureToggle'
import { logger } from '@/monitoring/logger'
import { FeatureToggle } from '@/resources/enums'
import { ConnectionStatus } from '@/resources/enums/websocket'
import { ActiveOrder, ActiveOrdersData } from '@/resources/types'
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { initWebSocket, setIsWebSocketEnabled } from './webSocketManager'
import { OrdersWebSocketContext } from './useOrdersWebSocket'

const OrdersWebSocketProvider = ({ children }: PropsWithChildren) => {
  const isWebSocketFeatureEnabled =
    useFeatureToggle({ splitName: 'seller_enable_wss' }) === FeatureToggle.ON

  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>(
    ConnectionStatus.NOT_STARTED
  )
  const [orders, setOrders] = useState<ActiveOrder[]>([])
  const [hasUpdatedOrders, setHasUpdatedOrders] = useState(false)
  const [hasInitializedWebSocket, setHasInitializedWebSocket] = useState(false)

  const isWebSocketConnected = useMemo(
    () => connectionStatus === ConnectionStatus.CONNECTED,
    [connectionStatus]
  )

  const isLoading = useMemo(
    () =>
      connectionStatus === ConnectionStatus.RETRYING ||
      connectionStatus === ConnectionStatus.STARTING,
    [connectionStatus]
  )

  const isWebSocketActive = useMemo(
    () => isWebSocketConnected && !isLoading,
    [isWebSocketConnected, isLoading]
  )

  const patchOrderStatus = useCallback(
    (orderNumber: string, status: string) => {
      setOrders(prev =>
        prev.map(order =>
          order.number === orderNumber ? { ...order, status } : order
        )
      )
    },
    []
  )

  const context = useMemo(
    () => ({
      orders,
      isWebSocketConnected,
      isLoading,
      isWebSocketActive,
      hasUpdatedOrders,
      patchOrderStatus,
    }),
    [
      orders,
      isWebSocketConnected,
      isLoading,
      isWebSocketActive,
      hasUpdatedOrders,
      patchOrderStatus,
    ]
  )

  const onMessageHandler = useCallback((ordersJson: string): void => {
    try {
      const parsed = JSON.parse(ordersJson) as ActiveOrdersData
      if (parsed?.data?.activeOrders) {
        setOrders(parsed.data.activeOrders)
        setHasUpdatedOrders(true)
      }
    } catch {
      logger.warn('[WebSocket] Failed to parse received orders')
    }
  }, [])

  const onConnectionStatusChange = useCallback((status: ConnectionStatus) => {
    setConnectionStatus(status)
    if (
      status === ConnectionStatus.RETRYING ||
      status === ConnectionStatus.FAILED
    ) {
      setHasUpdatedOrders(false)
      setOrders([])
    }
  }, [])

  useEffect(() => {
    if (!isWebSocketFeatureEnabled) {
      setConnectionStatus(ConnectionStatus.NOT_STARTED)
      setHasInitializedWebSocket(false)
      setIsWebSocketEnabled(false)
    }
  }, [isWebSocketFeatureEnabled])

  useEffect(() => {
    if (
      !hasInitializedWebSocket &&
      isWebSocketFeatureEnabled &&
      connectionStatus === ConnectionStatus.NOT_STARTED
    ) {
      setHasInitializedWebSocket(true)
      initWebSocket(
        isWebSocketFeatureEnabled,
        onConnectionStatusChange,
        onMessageHandler
      )
    }
  }, [
    hasInitializedWebSocket,
    onMessageHandler,
    onConnectionStatusChange,
    isWebSocketFeatureEnabled,
    connectionStatus,
  ])

  return (
    <OrdersWebSocketContext.Provider value={context}>
      {children}
    </OrdersWebSocketContext.Provider>
  )
}

export default OrdersWebSocketProvider
